import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Layers, Upload, Plus, Trash2, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NftArtTraits({ user }) {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [layers, setLayers] = useState([]);
  const [newTraitType, setNewTraitType] = useState('');
  const [quantity, setQuantity] = useState(10);
  const [generating, setGenerating] = useState(false);
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/nft/projects`, { headers: { Authorization: `Bearer ${token}` } });
        setProjects(res.data.projects || []);
      } catch (err) { /* silent */ }
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) fetchLayers();
  }, [selectedProject]);

  const fetchLayers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/nft/projects/${selectedProject}/traits/layers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLayers(res.data.layers || []);
    } catch (err) { /* silent */ }
  };

  const addLayer = async () => {
    if (!newTraitType.trim() || !selectedProject) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/nft/projects/${selectedProject}/traits/layers`,
        { trait_type: newTraitType.trim(), items: [] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewTraitType('');
      fetchLayers();
      toast.success(`Layer "${newTraitType.trim()}" added`);
    } catch (err) {
      toast.error('Failed to add layer');
    }
  };

  const uploadTraitImage = async (layerIndex, file) => {
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('project_id', selectedProject);
      const res = await axios.post(`${API}/nft/images/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      const asset = res.data.assets?.[0];
      if (!asset) throw new Error('Upload failed');

      const layer = layers[layerIndex];
      const newItem = {
        id: asset.id,
        name: file.name.replace(/\.[^/.]+$/, ''),
        image_url: asset.image_url,
        rarity_weight: 100,
      };
      const updatedItems = [...(layer.items || []), newItem];

      await axios.post(
        `${API}/nft/projects/${selectedProject}/traits/layers`,
        { trait_type: layer.trait_type, items: updatedItems },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchLayers();
      toast.success(`Uploaded "${newItem.name}" to ${layer.trait_type}`);
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && uploadTarget !== null) {
      uploadTraitImage(uploadTarget, file);
    }
    e.target.value = '';
  };

  const handleGenerate = async () => {
    if (!selectedProject) return;
    setGenerating(true);
    setPreviews([]);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/nft/projects/${selectedProject}/traits/generate`,
        { quantity, seed: Math.floor(Math.random() * 999999) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const mappings = (res.data.mappings || []).map((m) => ({
        ...m,
        full_url: `${process.env.REACT_APP_BACKEND_URL}${m.image_url}`,
      }));
      setPreviews(mappings);
      toast.success(`Generated ${res.data.count} images`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div data-testid="nft-art-traits-page">
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontFamily: theme.fonts.heading, fontSize: '28px', fontWeight: '700', color: theme.colors.text.primary, marginBottom: '8px' }}>
          Trait-Based Generator
        </h1>
        <p style={{ color: theme.colors.text.secondary, fontSize: '15px' }}>
          Upload layer images, define traits, and generate unique combinations.
        </p>
      </div>

      <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} onChange={handleFileSelect} />

      {/* Project Select */}
      <Card style={{ border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card, marginBottom: '24px' }}>
        <CardContent className="pt-5 pb-5">
          <Label style={{ color: theme.colors.text.primary, marginBottom: '8px', display: 'block' }}>Select Project</Label>
          <select
            data-testid="trait-project-select"
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            style={{ width: '100%', maxWidth: '400px', padding: '10px 12px', borderRadius: '8px', fontSize: '14px', background: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.2)', color: theme.colors.text.primary }}
          >
            <option value="">Choose a project...</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.quantity} items)</option>)}
          </select>
          {projects.length === 0 && (
            <p style={{ fontSize: '13px', color: theme.colors.text.muted, marginTop: '8px' }}>
              No projects found. Create an NFT project first.
            </p>
          )}
        </CardContent>
      </Card>

      {selectedProject && (
        <>
          {/* Layer Management */}
          <Card style={{ border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card, marginBottom: '24px' }}>
            <CardHeader>
              <CardTitle style={{ color: '#3B82F6', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Layers size={18} /> Trait Layers ({layers.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add new layer */}
              <div style={{ display: 'flex', gap: '8px' }}>
                <Input
                  data-testid="new-trait-type-input"
                  value={newTraitType}
                  onChange={(e) => setNewTraitType(e.target.value)}
                  placeholder="e.g. Background, Body, Eyes, Hat"
                  onKeyDown={(e) => e.key === 'Enter' && addLayer()}
                  style={{ background: 'rgba(59, 130, 246, 0.05)', borderColor: 'rgba(59, 130, 246, 0.2)', color: theme.colors.text.primary }}
                />
                <Button data-testid="add-layer-btn" onClick={addLayer} style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#3B82F6', whiteSpace: 'nowrap' }}>
                  <Plus size={16} /> Add Layer
                </Button>
              </div>

              {layers.length === 0 ? (
                <p style={{ color: theme.colors.text.muted, fontSize: '13px', textAlign: 'center', padding: '20px' }}>
                  No layers yet. Add trait types above (e.g. Background, Body, Eyes).
                </p>
              ) : (
                layers.map((layer, li) => (
                  <div
                    key={layer.id || li}
                    data-testid={`trait-layer-${layer.trait_type}`}
                    style={{ padding: '16px', borderRadius: '8px', border: `1px solid ${theme.colors.borders}`, background: 'rgba(59, 130, 246, 0.02)' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <p style={{ fontWeight: '600', color: theme.colors.text.primary, fontSize: '14px' }}>
                        {layer.trait_type} <span style={{ fontWeight: '400', color: theme.colors.text.muted }}>({(layer.items || []).length} variants)</span>
                      </p>
                      <Button
                        size="sm"
                        onClick={() => { setUploadTarget(li); fileInputRef.current?.click(); }}
                        disabled={uploading}
                        style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3B82F6', fontSize: '12px' }}
                      >
                        <Upload size={14} /> {uploading && uploadTarget === li ? 'Uploading...' : 'Add Variant'}
                      </Button>
                    </div>
                    {(layer.items || []).length > 0 && (
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {layer.items.map((item) => (
                          <div key={item.id} style={{ textAlign: 'center' }}>
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}${item.image_url}`}
                              alt={item.name}
                              style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '6px', border: `1px solid ${theme.colors.borders}` }}
                            />
                            <p style={{ fontSize: '11px', color: theme.colors.text.muted, marginTop: '4px', maxWidth: '64px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {item.name}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Generate */}
          <Card style={{ border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card, marginBottom: '24px' }}>
            <CardHeader>
              <CardTitle style={{ color: '#3B82F6' }}>Generate Collection</CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                <div>
                  <Label style={{ color: theme.colors.text.primary }}>Quantity</Label>
                  <Input
                    data-testid="trait-quantity-input"
                    type="number" min={1} max={500} value={quantity}
                    onChange={(e) => setQuantity(Math.min(500, Math.max(1, parseInt(e.target.value) || 1)))}
                    style={{ width: '120px', background: 'rgba(59, 130, 246, 0.05)', borderColor: 'rgba(59, 130, 246, 0.2)', color: theme.colors.text.primary }}
                  />
                </div>
                <Button
                  data-testid="trait-generate-btn"
                  onClick={handleGenerate}
                  disabled={generating || layers.length === 0}
                  style={{ background: 'linear-gradient(135deg, #3B82F6, #06B6D4)', color: '#FFF', padding: '10px 24px', fontWeight: '600' }}
                >
                  {generating ? 'Generating...' : 'Generate Collection Previews'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Previews */}
          {previews.length > 0 && (
            <Card style={{ border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card }}>
              <CardHeader>
                <CardTitle style={{ color: theme.colors.text.primary }}>Preview ({previews.length} images)</CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px', maxHeight: '500px', overflowY: 'auto' }}>
                  {previews.map((p) => (
                    <div key={p.token_id} data-testid={`trait-preview-${p.token_id}`} style={{ borderRadius: '8px', overflow: 'hidden', border: `1px solid ${theme.colors.borders}` }}>
                      <img src={p.full_url} alt={`Token #${p.token_id}`} style={{ width: '100%', height: '150px', objectFit: 'cover', display: 'block' }} />
                      <div style={{ padding: '6px 8px' }}>
                        <p style={{ fontSize: '12px', fontWeight: '600', color: theme.colors.text.primary }}>#{p.token_id}</p>
                        <p style={{ fontSize: '10px', color: theme.colors.text.muted }}>
                          {(p.traits || []).map((t) => t.value).join(', ')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
